import he from "he";

interface NavigatorUAData {
  readonly platform: string;
  readonly mobile: boolean;
  readonly brands: Array<{ brand: string; version: string }>;
}

declare global {
  interface Navigator {
    userAgentData?: NavigatorUAData;
  }
}

export interface ILeadTracking {
  trackingUtmSource?: string;
  trackingUtmMedium?: string;
  trackingUtmCampaign?: string;
  trackingUtmContent?: string;
  trackingClickId?: string;
  trackingReferrer?: string;
  trackingKeywords?: string;
  trackingUtmTerm?: string;
  trackingSubmittedAt?: string;
  trackingPartner?: string;
  trackingPartnerId?: string;
  trackingPartnerRef?: string;
  trackingPlatform?: string;
  trackingDeviceType?: string;
  trackingBrowser?: string;
  trackingBrowserVersion?: string;
  trackingAgent?: string;
  trackingDevice?: string;
  trackingIpAddress?: string;
  trackingUrlId?: string;
}

export function setLeadTrackingData(clientData?: {
  regSrc?: string;
  trackingUrlId?: string;
}): ILeadTracking {
  const userAgent = navigator.userAgent;
  const decodedUrl = he.decode(clientData?.trackingUrlId || window.location.href);
  const searchParams = new URL(decodedUrl, window.location.origin).searchParams;
  const cookieConsent = localStorage.getItem("cookieConsent");

  const lowerCaseParams = new Map<string, string>();
  searchParams.forEach((value, key) => {
    lowerCaseParams.set(key.toLowerCase(), value.trim());
  });

  const getParamPriority = (keys: string[]): string | undefined => {
    for (const key of keys) {
      const val = lowerCaseParams.get(key.toLowerCase());
      if (val) return val;
    }
    return undefined;
  };

  const deviceType = /mobile/i.test(userAgent) ? "Mobile" : /tablet/i.test(userAgent) ? "Tablet" : "Desktop";

  const trackingData: ILeadTracking = {
    trackingUtmSource:
      getParamPriority(["utm_source", "vt_source"]) ||
      clientData?.regSrc ||
      "Portal",
    trackingUtmMedium:
      getParamPriority(["utm_medium", "vt_medium"]) ||
      (deviceType === "Mobile" ? "searchmobile" : "search"),
    trackingUtmCampaign: getParamPriority(["utm_campaign", "vt_campaign"]),
    trackingUtmContent: getParamPriority(["utm_content", "vt_content"]),
    trackingReferrer: getParamPriority(["utm_referrer", "vt_referrer"]),
    trackingKeywords: getParamPriority(["utm_keyword", "vt_keyword", "utm_keywords", "tracking_keywords"]),
    trackingUtmTerm: getParamPriority(["utm_term", "vt_term"]),
    trackingSubmittedAt: new Date().toISOString(),
    trackingPartner: getParamPriority(["tracking_partner", "vt_partner"]),
    trackingPartnerId: getParamPriority(["tracking_partnerid", "vt_partnerid"]),
    trackingPartnerRef: getParamPriority(["tracking_partnerref"]),
    trackingUrlId: decodedUrl,
    trackingClickId: getParamPriority(["msclkid", "gclid", "fbclid", "tracking_clickid"]),
    // Empty strings if essential cookies selected
    trackingPlatform: "",
    trackingDeviceType: "",
    trackingBrowser: "",
    trackingBrowserVersion: "",
    trackingAgent: "",
    trackingDevice: "",
    trackingIpAddress: "",
  };

  if (cookieConsent === "acceptedAll") {
    trackingData.trackingPlatform = getPlatform(userAgent);
    trackingData.trackingDeviceType = deviceType;
    trackingData.trackingBrowser = getBrowser(userAgent);
    trackingData.trackingBrowserVersion = getBrowserVersion(userAgent);
    trackingData.trackingAgent = userAgent;
    trackingData.trackingDevice = getDeviceName(userAgent);
    trackingData.trackingIpAddress = "";
  }

  return trackingData;
}

function getDeviceName(userAgent: string): string {
  if (/iPhone/.test(userAgent)) return "iPhone";
  if (/iPad/.test(userAgent)) return "iPad";
  if (/Macintosh/.test(userAgent)) return "Mac";
  if (/Android/.test(userAgent)) return "Android Phone";
  if (/Windows NT/.test(userAgent)) return "Windows PC";
  if (/Linux/.test(userAgent)) return "Linux PC";
  return "Unknown";
}

function getBrowser(userAgent: string): string {
  if (userAgent.includes("Edg")) return "Edge";
  if (userAgent.includes("Firefox")) return "Firefox";
  if (userAgent.includes("Chrome")) return "Chrome";
  if (userAgent.includes("Safari") && !userAgent.includes("Chrome")) return "Safari";
  if (userAgent.includes("Opera") || userAgent.includes("OPR")) return "Opera";
  if (userAgent.includes("MSIE") || userAgent.includes("Trident")) return "Internet Explorer";
  if (userAgent.includes("SamsungBrowser")) return "Samsung Internet";
  if (userAgent.includes("Brave")) return "Brave";
  return "Unknown";
}

function getBrowserVersion(userAgent: string): string {
  const patterns = [
    { name: "Edg", regex: /(Edg|Edge)\/([\d.]+)/, index: 2 },
    { name: "Chrome", regex: /(Chrome)\/([\d.]+)/, index: 2 },
    { name: "Firefox", regex: /(Firefox)\/([\d.]+)/, index: 2 },
    { name: "Safari", regex: /Version\/([\d.]+) Safari\/([\d.]+)/, index: 1 },
    { name: "Opera", regex: /(Opera|OPR)\/([\d.]+)/, index: 2 },
    { name: "IE", regex: /(MSIE |rv:)([\d.]+)/, index: 2 },
    { name: "Samsung", regex: /SamsungBrowser\/([\d.]+)/, index: 1 },
  ];

  for (const p of patterns) {
    const match = userAgent.match(p.regex);
    if (match) return match[p.index];
  }
  return "Unknown";
}

function getPlatform(userAgent: string): string {
  if (navigator.userAgentData?.platform) {
    return navigator.userAgentData.platform;
  }
  if (userAgent.includes("Win")) return "Windows";
  if (userAgent.includes("Mac")) return "MacOS";
  if (userAgent.includes("Linux")) return "Linux";
  if (userAgent.includes("Android")) return "Android";
  if (/iPhone|iPad/.test(userAgent)) return "iOS";
  return "Unknown";
}
